import {
  MedicalOrderReviewStatus,
  MedicalOrderStatus,
} from "../../../../enums/orderReviewStatus";
import { IconHoverText, IconNames } from "../../../../enums/orderStatusIcons";
import { UserGeneralInfoContextKeys } from "../../../../enums/userGeneralInfoContextKeys";
import { UserStore } from "../../../../store/UserGeneralInfoStore";

/**
 * Builds the status/review icon list for a worklist order-status column.
 *
 * When `queryRaisedByAgent` is true, returns only the agent-query icon and
 * skips normal status/review mapping (query raised by agent takes precedence).
 * Review icons are included only when facility review mode is enabled.
 *
 * @param status - Medical order process status, if known
 * @param reviewStatus - Medical order review status, if known
 * @param queryRaisedByAgent - True when master auth is query and the order was
 *   processed by an agent (`ai_agent_type === "agent"`)
 * @returns Object with an `icons` array of `{ icon_id, hover_text }` entries
 */
export const getOrderIcons = (
  status?: MedicalOrderStatus,
  reviewStatus?: MedicalOrderReviewStatus,
  queryRaisedByAgent?: boolean,
) => {
  const icons: { icon_id: IconNames; hover_text: IconHoverText }[] = [];
  const enableReviewMode = UserStore.getItem(
    UserGeneralInfoContextKeys.ENABLE_REVIEW_MODE,
  );

  const statusToIconMap: Record<
    MedicalOrderStatus,
    { icon_id: IconNames; hover_text: IconHoverText }
  > = {
    [MedicalOrderStatus.YET_TO_START_WORK_ON_ORDER]: {
      icon_id: IconNames.ORDER_PROCESS_YET_TO_START,
      hover_text: IconHoverText.ORDER_PROCESS_YET_TO_START,
    },
    [MedicalOrderStatus.ORDER_IN_PROGRESS]: {
      icon_id: IconNames.ORDER_PROCESS_IN_PROGRESS,
      hover_text: IconHoverText.ORDER_PROCESS_IN_PROGRESS,
    },
    [MedicalOrderStatus.ORDER_COMPLETED_BY_HUMAN]: {
      icon_id: IconNames.ORDER_PROCESS_SUCCESS,
      hover_text: IconHoverText.ORDER_PROCESS_SUCCESS,
    },
    [MedicalOrderStatus.ORDER_COMPLETED_BY_AGENT]: {
      icon_id: IconNames.ORDER_PROCESS_SUCCESS_BY_AGENT,
      hover_text: IconHoverText.ORDER_PROCESS_SUCCESS_BY_AGENT,
    },
    // Agent-in-progress uses the same in-flight icon as human in-progress orders.
    [MedicalOrderStatus.AGENT_IN_PROGRESS]: {
      icon_id: IconNames.ORDER_PROCESS_IN_PROGRESS,
      hover_text: IconHoverText.ORDER_PROCESS_IN_PROGRESS,
    },
  };

  const reviewToIconMap: Record<
    MedicalOrderReviewStatus,
    { icon_id: IconNames; hover_text: IconHoverText }
  > = {
    [MedicalOrderReviewStatus.ORDER_IN_REVIEW]: {
      icon_id: IconNames.ORDER_IN_REVIEW,
      hover_text: IconHoverText.ORDER_IN_REVIEW,
    },
    [MedicalOrderReviewStatus.ORDER_PASSED]: {
      icon_id: IconNames.ORDER_REVIEW_APPROVED,
      hover_text: IconHoverText.ORDER_REVIEW_APPROVED,
    },
    [MedicalOrderReviewStatus.ORDER_REJECTED]: {
      icon_id: IconNames.ORDER_REVIEW_REJECTED,
      hover_text: IconHoverText.ORDER_REVIEW_REJECTED,
    },
    [MedicalOrderReviewStatus.AGENT_ORDER_REVIEW_PENDING]: {
      icon_id: IconNames.AGENT_ORDER_REVIEW_PENDING,
      hover_text: IconHoverText.AGENT_ORDER_REVIEW_PENDING,
    },
    [MedicalOrderReviewStatus.AGENT_ORDER_PASSED_BY_HUMAN]: {
      icon_id: IconNames.ORDER_REVIEW_APPROVED,
      hover_text: IconHoverText.ORDER_REVIEW_APPROVED,
    },
    [MedicalOrderReviewStatus.AGENT_ORDER_REJECTED_BY_HUMAN]: {
      icon_id: IconNames.ORDER_REVIEW_REJECTED,
      hover_text: IconHoverText.ORDER_REVIEW_REJECTED,
    },
  };

  const queryRaisedByAgentToIconMap: {
    icon_id: IconNames;
    hover_text: IconHoverText;
  } = {
    icon_id: IconNames.AGENT_ORDER_QUERY_RAISED,
    hover_text: IconHoverText.AGENT_ORDER_QUERY_RAISED,
  };

  if (queryRaisedByAgent) {
    icons.push(queryRaisedByAgentToIconMap);
    return { icons };
  }

  if (status) icons.push(statusToIconMap[status]);

  if (reviewStatus && enableReviewMode)
    icons.push(reviewToIconMap[reviewStatus]);

  return { icons };
};
